/**
 * Material Card Component
 *
 * Displays a learning material in a grid-friendly card format
 * with type indicator, duration, and assignment stats.
 */

import { Video, FileText, Users, Link2 } from 'lucide-react';
import type { LearningMaterial } from '../../../hooks/useLearning';

interface MaterialCardProps {
  material: LearningMaterial;
  onClick: () => void;
}

export function MaterialCard({ material, onClick }: MaterialCardProps) {
  const isVideo = material.materialType === 'video';

  // Format duration (seconds to human-readable)
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return null;
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes} min`;
  };

  return (
    <button
      onClick={onClick}
      className="group relative flex flex-col rounded-2xl border border-gray-100 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:border-guild-green/30 hover:shadow-lg hover:shadow-guild-green/5 focus:outline-none focus:ring-2 focus:ring-guild-green/20 focus:ring-offset-2"
    >
      {/* Type Icon & Badge */}
      <div className="mb-4 flex items-start justify-between">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-xl ${
            isVideo
              ? 'bg-gradient-to-br from-violet-500/10 to-violet-600/10 text-violet-600'
              : 'bg-gradient-to-br from-amber-500/10 to-amber-600/10 text-amber-600'
          }`}
        >
          {isVideo ? <Video size={24} strokeWidth={1.5} /> : <FileText size={24} strokeWidth={1.5} />}
        </div>

        <div className="flex items-center gap-2">
          {material.duration && isVideo && (
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
              {formatDuration(material.duration)}
            </span>
          )}
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              isVideo
                ? 'bg-violet-100 text-violet-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {isVideo ? 'Video' : 'Document'}
          </span>
        </div>
      </div>

      {/* Title */}
      <h3 className="mb-2 line-clamp-2 text-base font-semibold text-gray-900 group-hover:text-guild-green">
        {material.title}
      </h3>

      {/* Description */}
      {material.description && (
        <p className="mb-4 line-clamp-2 text-sm text-gray-500">
          {material.description}
        </p>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Footer Stats */}
      <div className="mt-4 flex items-center justify-between border-t border-gray-50 pt-4">
        {/* Assignment Count */}
        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          <Users size={14} className="text-gray-400" />
          <span className="font-medium">{material.assignmentCount}</span>
          <span>assigned</span>
          {material.completionRate !== null && (
            <span className="ml-1 text-emerald-600">
              ({material.completionRate}% done)
            </span>
          )}
        </div>

        {/* Linked SOP */}
        {material.sop && (
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <Link2 size={12} />
            <span className="max-w-[100px] truncate">{material.sop.title}</span>
          </div>
        )}
      </div>

      {/* Hover indicator */}
      <div className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 rounded-b-2xl bg-gradient-to-r from-guild-green to-guild-mint transition-transform duration-300 group-hover:scale-x-100" />
    </button>
  );
}
